import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth, newQwanta, deleteQwanta } from 'qwanyx';

export const QwanyxContext = createContext();

export const QwanyxProvider = ({ children }) => {
  const { currentUser } = useAuth();
  
  // State to store domain ID (di)
  const [domainId, setDomainId] = useState(null);
  
  // State to store _id, ed, di, and pa
  const [selectedCard, setSelectedCard] = useState({
    _id: null,
    ed: null,
    di: null,
    pa: null,
  });
  
  // New state for card registry indexed by cardName
  const [cardRegistry, setCardRegistry] = useState({});
  
  // New state to control card visibility
  const [isCardVisible, setIsCardVisible] = useState(true);

  // Debug: Log state changes
  useEffect(() => {
    console.log("🔵 QwanyxProvider - selectedCard updated:", selectedCard);
  }, [selectedCard]);
  
  // Log visibility changes
  useEffect(() => {
    console.log("🔵 QwanyxProvider - card visibility:", isCardVisible ? "Visible" : "Hidden");
  }, [isCardVisible]);

  // Initialize domainId with currentUser.id when component mounts
  useEffect(() => {
    if (currentUser && currentUser.id) {
      setDomainId(currentUser.id);
    }
  }, [currentUser]);

  // Register a card with the registry
  const registerCard = useCallback((cardName, cardData) => {
    if (!cardName) {
      console.error("Cannot register card without cardName");
      return;
    }
    
    setCardRegistry(prev => ({
      ...prev,
      [cardName]: {
        ...cardData,
        cardName, // Ensure cardName is included in the data
        registeredAt: Date.now()
      }
    }));
    
    console.log(`🔵 Registered card: ${cardName}`, cardData);
  }, []);
  
  // Get a card from the registry by its cardName
  const getCardByName = useCallback((cardName) => {
    if (!cardName || !cardRegistry[cardName]) {
      return null;
    }
    return cardRegistry[cardName];
  }, [cardRegistry]);
  
  // Update a card in the registry by its cardName
  const updateCardByName = useCallback((cardName, updates) => {
    if (!cardName || !cardRegistry[cardName]) {
      console.error(`Card not found in registry: ${cardName}`);
      return;
    }
    
    setCardRegistry(prev => ({
      ...prev,
      [cardName]: {
        ...prev[cardName],
        ...updates,
        lastUpdated: Date.now()
      }
    }));
    
    console.log(`🔵 Updated card: ${cardName}`, updates);
    
    // If this is also the currently selected card, update that state too
    if (selectedCard.cardName === cardName) {
      setSelectedCard(prev => ({
        ...prev,
        ...updates
      }));
    }
  }, [cardRegistry, selectedCard]);

  // Enhanced update function with memoization to prevent unnecessary renders
  const updateSelectedCard = useCallback((newCardData) => {
    console.log("🔵 updateSelectedCard called with:", newCardData);
    
    // Use functional update to ensure we're working with the latest state
    setSelectedCard(prevState => {
      // Check if this is actually a change to avoid unnecessary renders
      const isIdentical = 
        prevState._id === newCardData._id &&
        prevState.ed === newCardData.ed &&
        prevState.di === newCardData.di &&
        prevState.pa === newCardData.pa;
      
      if (isIdentical) {
        console.log("🔄 Forcing re-render despite identical values");
        // Even though values are identical, force a new reference
        return { ...newCardData, _forceUpdate: Date.now() };
      }
      
      console.log("✨ New card data is different, updating state");
      return { ...newCardData };
    });
    
    // If cardName is provided, also register/update this card in the registry
    if (newCardData.cardName) {
      registerCard(newCardData.cardName, newCardData);
    }
    
    // Force a DOM update in the next event loop
    setTimeout(() => {
      console.log("⏱️ State update complete, selectedCard should now be:", newCardData);
    }, 0);
  }, [registerCard]);

  // Function to update individual card properties
  const updateCardProperty = useCallback((property, value, cardName = null) => {
    if (!['_id', 'di', 'ed', 'pa'].includes(property)) {
      console.error(`Invalid card property: ${property}`);
      return;
    }
    
    // If cardName is provided, update that specific card in the registry
    if (cardName) {
      updateCardByName(cardName, { [property]: value });
      return;
    }
    
    // Otherwise update the selected card
    setSelectedCard(prevCard => ({
      ...prevCard,
      [property]: value
    }));
    
    console.log(`🔵 Updated card ${property} to:`, value);
  }, [updateCardByName]);

  // Function to show a specific card or the current card
  const showCard = useCallback((cardName = null) => {
    if (cardName) {
      updateCardByName(cardName, { isVisible: true });
      console.log(`🔵 Card ${cardName} set to visible`);
      return;
    }
    
    setIsCardVisible(true);
    console.log("🔵 Current card set to visible");
  }, [updateCardByName]);

  // Function to hide a specific card or the current card
  const hideCard = useCallback((cardName = null) => {
    if (cardName) {
      updateCardByName(cardName, { isVisible: false });
      console.log(`🔵 Card ${cardName} set to hidden`);
      return;
    }
    
    setIsCardVisible(false);
    console.log("🔵 Current card set to hidden");
  }, [updateCardByName]);

  // Toggle visibility for a specific card or the current card
  const toggleCardVisibility = useCallback((cardName = null) => {
    if (cardName && cardRegistry[cardName]) {
      const currentVisibility = cardRegistry[cardName].isVisible !== false;
      updateCardByName(cardName, { isVisible: !currentVisibility });
      console.log(`🔵 Card ${cardName} visibility toggled to: ${!currentVisibility}`);
      return;
    }
    
    setIsCardVisible(prev => !prev);
    console.log("🔵 Current card visibility toggled");
  }, [cardRegistry, updateCardByName]);

  const updateDomainId = useCallback((newDomainId) => {
    setDomainId(newDomainId);
  }, []);

  const newCard = useCallback(() => {
    const newCardData = newQwanta(selectedCard.ed, domainId, selectedCard.pa, "instance");
    setSelectedCard(newCardData);
  }, [selectedCard.ed, domainId, selectedCard.pa]);

  const deleteCard = useCallback((cardName = null) => {
    // Use the built-in confirm dialog
    const confirmDelete = window.confirm("Are you sure you want to delete this card?");

    // Only proceed with deletion if the user confirmed
    if (confirmDelete) {
      if (cardName && cardRegistry[cardName]) {
        // Delete specific card by name from registry
        const cardToDelete = cardRegistry[cardName];
        
        if (cardToDelete._id) {
          deleteQwanta(cardToDelete._id);
          
          // Remove from registry
          setCardRegistry(prev => {
            const newRegistry = { ...prev };
            delete newRegistry[cardName];
            return newRegistry;
          });
          
          console.log(`🔵 Deleted card: ${cardName}`);
        }
      } else {
        // Delete selected card
        deleteQwanta(selectedCard._id);
        setSelectedCard({
          _id: null,
          ed: null,
          di: null,
          pa: null,
        });
      }
    }
  }, [selectedCard._id, cardRegistry]);

  const contextValue = {
    // Card data
    selectedCard,
    domainId,
    
    // Card registry
    cardRegistry,
    
    // Card visibility
    isCardVisible,
    
    // Registry functions
    registerCard,
    getCardByName,
    updateCardByName,
    
    // Update functions
    updateSelectedCard,
    updateCardProperty,
    updateDomainId,
    
    // Visibility control functions
    showCard,
    hideCard,
    toggleCardVisibility,
    
    // Card creation/deletion
    newCard,
    deleteCard
  };

  return (
    <QwanyxContext.Provider value={contextValue}>
      {children}
    </QwanyxContext.Provider>
  );
};

export const useQwanyx = () => {
  const context = useContext(QwanyxContext);
  if (!context) {
    throw new Error('useQwanyx must be used within a QwanyxProvider');
  }
  return context;
};

export default QwanyxProvider;